import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import RelatedArticles from "@/components/RelatedArticles";
import { ChevronRight, Lightbulb, ExternalLink, ShieldCheck } from "lucide-react";

export default function ArticleLayout({ article, children }) {
  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-10 lg:py-16 overflow-x-hidden">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground mb-8">
        <Link to="/" className="hover:text-accent transition-colors">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/solar-guides" className="hover:text-accent transition-colors">Solar Guides</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground truncate max-w-[200px]">{article.title}</span>
      </nav>

      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {article.tags?.slice(0, 5).map((tag) => (
            <span key={tag} className="text-[10px] font-mono bg-secondary border border-border px-2.5 py-1 rounded-full text-muted-foreground uppercase tracking-wider">
              {tag}
            </span>
          ))}
        </div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4 text-xs text-muted-foreground">
          <span>
            By <Link to="/about#editorial-team" className="font-medium text-foreground hover:text-accent">Highest Efficiency Solar Cell Editorial Team</Link>
          </span>
          <span aria-hidden="true">•</span>
          <span>Research-based guide</span>
          {article.updatedAt && (
            <>
              <span aria-hidden="true">•</span>
              <span>Reviewed {new Date(article.updatedAt).toLocaleDateString()}</span>
            </>
          )}
        </div>
        <p className="text-lg text-muted-foreground leading-relaxed">{article.intro}</p>
        {article.heroImageUrl && (
          <figure className="mt-7 overflow-hidden rounded-2xl border border-border bg-secondary">
            <img
              src={article.heroImageUrl}
              alt={article.heroImageAlt || article.title}
              className="w-full aspect-video object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </figure>
        )}
      </header>

      {/* Quick Answer */}
      {article.quickAnswer && (
        <div className="bg-accent/10 border border-accent/30 rounded-2xl p-5 mb-8">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-accent shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-mono font-bold text-accent uppercase tracking-widest mb-1.5">Quick Answer</p>
              <p className="text-sm leading-relaxed">{article.quickAnswer}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="prose-like space-y-8 mb-8 min-w-0">
        {children}

        {/* Sections */}
        {article.sections?.map((section, i) => (
          <div key={i}>
            <h2 className="font-heading font-bold text-xl lg:text-2xl mb-3">{section.heading}</h2>
            <p className="text-muted-foreground leading-relaxed">{section.content}</p>
          </div>
        ))}
      </div>

      {/* FAQ Section */}
      {article.faqs?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading font-bold text-2xl mb-5">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {article.faqs.map((faq, i) => (
              <div key={i} className="border border-border rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-2 text-base">{faq.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources & editorial methodology */}
      {article.sourceNotes?.length > 0 && (
        <section className="mb-8 rounded-2xl border border-border bg-secondary/20 p-5 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading font-bold text-xl">Sources & Editorial Method</h2>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                We compare primary technical references, manufacturer documentation, and current product specifications. Amazon is used as a purchase destination, not as evidence for ratings or technical claims. Unless a page explicitly documents hands-on testing, recommendations are specification-based editorial analysis.
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {article.sourceNotes.map((source) => (
              <li key={source.url}>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-2 text-sm text-accent-text hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{source.label}</span>
                </a>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-4">
            See our <Link to="/about#editorial-process" className="underline hover:text-foreground">editorial process and AI-assisted content policy</Link>.
          </p>
        </section>
      )}

      {/* Affiliate Disclosure */}
      <AffiliateDisclosure />

      {/* Related Articles */}
      <RelatedArticles currentSlug={article.slug} relatedSlugs={article.relatedSlugs} />

      {/* Related internal links */}
      {article.relatedPaths?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-border">
          <h3 className="font-heading font-bold text-base mb-4">More Buyer Guides</h3>
          <div className="flex flex-wrap gap-2">
            {article.relatedPaths.map((path) => (
              <Link
                key={path}
                to={path}
                className="text-sm px-3 py-1.5 bg-secondary border border-border rounded-full hover:border-accent/40 hover:text-accent transition-colors font-mono"
              >
                {path.replace(/\//g, "").replace(/-/g, " ")}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}